// ABOUTME: i18next setup. Translations load per language on demand (one JSON per language and namespace),
// ABOUTME: so the initial bundle carries no locale data; `i18nReady` resolves once the first language is in.
import i18n from "i18next"
import type { BackendModule, ReadCallback, ResourceKey } from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"

// Vite turns each glob entry into its own chunk, fetched only when that language is requested.
// Both folders feed the single "translation" namespace, exactly as the eager `resources` object used to.
/** The catalogue the language selectors offer — independent of which bundles have loaded. UI order. */
export const SUPPORTED_LANGUAGES: readonly string[] = ["en", "ru", "fr", "tr", "de"]

/** Native names for the selectors: a language's own bundle is not loaded until it is chosen, so its name cannot come from there. */
export const LANGUAGE_NAMES: Readonly<Record<string, string>> = {
  en: "English",
  ru: "Русский",
  fr: "Français",
  tr: "Türkçe",
  de: "Deutsch",
}

/**
 * Which languages to load besides `code`. i18next hands over the detected code as is (`ru-RU` even with
 * `load: "languageOnly"`), so the check is on the base language. A supported language needs no fallback:
 * check:translations keeps every key present in all five, and loading "en" next to "ru" would only add ~200 KB.
 * Anything else (an unsupported browser language, nothing detected) resolves to "en" — with `fallbackLng: false`
 * i18next would resolve to no language at all and render keys.
 */
export const fallbackLanguagesFor = (code?: string): string[] => {
  // i18next calls this with no code at all when nothing matched supportedLngs
  // (node_modules/i18next/dist/esm/i18next.js:976 → getFallbackCodes(fallbackLng) without an argument).
  if (!code) return ["en"]
  const base = code.split("-")[0] ?? code
  return SUPPORTED_LANGUAGES.includes(base) ? [] : ["en"]
}

const mainByLanguage = import.meta.glob<{ default: ResourceKey }>("./frontWeb/*.json")
const errorsByLanguage = import.meta.glob<{ default: ResourceKey }>("./gqlErrors/*.json")

const loadTranslation = async (language: string): Promise<ResourceKey> => {
  const mainKey = `./frontWeb/${language}.json`
  const errorsKey = `./gqlErrors/${language}.json`
  const loadMain = mainByLanguage[mainKey]
  const loadErrors = errorsByLanguage[errorsKey]
  if (!loadMain || !loadErrors) {
    throw new Error(`No translations for "${language}"`)
  }
  const [mainModule, errorsModule] = await Promise.all([loadMain(), loadErrors()])
  return { ...(mainModule.default as object), ...(errorsModule.default as object) }
}

const lazyResources: BackendModule = {
  type: "backend",
  init: () => {},
  read: (language: string, _namespace: string, callback: ReadCallback) => {
    loadTranslation(language).then(
      (data) => callback(null, data),
      (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), null),
    )
  },
}

/**
 * Resolves when i18next has initialised and the detected language's translations are loaded. Never rejects:
 * LanguageProvider suspends on it above every error boundary, and a failed translation fetch must degrade to
 * untranslated keys, not to a blank screen.
 */
export const i18nReady: Promise<void> = i18n
  .use(LanguageDetector)
  .use(lazyResources)
  .use(initReactI18next)
  .init({
    debug: false,
    load: "languageOnly",
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    fallbackLng: fallbackLanguagesFor,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    detection: {
      order: ["querystring", "cookie", "localStorage", "sessionStorage", "navigator", "htmlTag"],
      lookupQuerystring: "lng",
      lookupCookie: "i18next",
      lookupLocalStorage: "i18nextLng",
      lookupSessionStorage: "i18nextLng",

      // cache user language
      caches: ["localStorage"],
      excludeCacheFor: ["cimode"],
    },
  })
  .then(
    () => undefined,
    () => undefined,
  )

export default i18n
